import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Collapse,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  useCallback,
  useState,
  type FunctionComponent,
  type ReactNode,
} from "react";

interface CollapsiblePanelProps {
  title: string;
  // Controls that belong on the title row, e.g. a clear button. Clicks on them
  // don't toggle the panel
  action?: ReactNode;
  children: ReactNode;
}

/**
 * The outer level of the left column: a whole feature panel that can fold away
 * to a single title row.
 *
 * The groups inside a panel already collapse on their own (they're
 * Accordions), so this gives the column a second, coarser level of folding:
 * collapse one panel to make room without losing the feature from the
 * navigator.
 */
const CollapsiblePanel: FunctionComponent<CollapsiblePanelProps> = ({
  title,
  action,
  children,
}) => {
  const theme = useTheme();
  const [isExpanded, setExpanded] = useState(true);

  const toggleExpanded = useCallback(() => {
    setExpanded((isExpanded) => !isExpanded);
  }, []);

  return (
    <>
      {/* Title row */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        px={1}
        py={0.5}
        sx={{
          cursor: "pointer",
          borderBottom: 1,
          borderColor: "divider",
          userSelect: "none",
          "&:hover": { backgroundColor: "action.hover" },
        }}
        onClick={toggleExpanded}
      >
        <IconButton
          size="small"
          aria-label={`toggle-panel-${title}`}
          aria-expanded={isExpanded}
          onClick={(event) => {
            // The whole row already toggles, so don't let the button toggle
            // it a second time
            event.stopPropagation();
            toggleExpanded();
          }}
        >
          <ExpandMoreIcon
            fontSize="small"
            sx={{
              transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
              transition: theme.transitions.create("transform", {
                duration: theme.transitions.duration.shortest,
              }),
            }}
          />
        </IconButton>

        <Typography variant="subtitle1" flexGrow={1} noWrap>
          {title}
        </Typography>

        {/* Actions must not fold the panel when clicked */}
        {action !== undefined && (
          <Box
            display="flex"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            {action}
          </Box>
        )}
      </Stack>

      {/* Body */}
      <Collapse in={isExpanded} unmountOnExit>
        {children}
      </Collapse>
    </>
  );
};

export default CollapsiblePanel;
